export type UserRole = 'citizen' | 'registrar' | 'judge' | 'admin';
export type UserStatus = 'active' | 'inactive' | 'pending';

/** A user row from GET /api/Admin/users */
export interface AdminUser {
  userId: string;
  email: string;
  role: string; // "Citizen", "Registrar", "Judge", "Admin"
  profileId: string | null;
  payerId: string | null;
  isApproved: boolean;
  createdAtUtc: string;
}

/** A row from GET /api/Admin/payments */
export interface AdminPayment {
  id: string;
  caseFilingId: string;
  caseReference: string | null;
  rrr: string;
  amount: number;
  status: number; // 0 Pending · 1 Unpaid · 2 Paid
  expiresAtUtc: string | null;
  isOrphan: boolean;
}

/** A single workflow-stage count from the dashboard */
export interface WorkflowSnapshotItem {
  label: string;
  value: number;
}

/** GET /api/Admin/dashboard — operations summary metrics */
export interface AdminDashboardStats {
  totalCases: number;
  pendingRegistrarReview: number;
  slaBreaches: number;
  closedCases: number;
  totalPaid: number;
  aiFlaggedCases: number;
  totalCasesAllTime: number;
  totalCasesThisMonth: number;
  activeCases: number;
  slaCompliance30d: number;
  slaCompliance30dChangePercent: number;
  revenueCollected30d: number;
  revenueCollected30dChangePercent: number;
  aiAccuracyVerified: number;
  averageRegistrarReviewHours: number;
  bottleneckAlerts: unknown[];
  workflowSnapshot: WorkflowSnapshotItem[];
}

/** A single { label, value } point from the GET /api/Admin/analytics/* series */
export interface AnalyticsPoint {
  label: string;
  value: number;
}

/** A row from GET /api/Admin/audit */
export interface AuditEvent {
  id: string;
  caseFilingId: string | null;
  actorId: string | null;
  actorRole: string;
  eventType: string;
  previousState: string;
  newState: string;
  metadataJson: string;
  createdAtUtc: string;
}

// ── Dashboard ─────────────────────────────────────────
export const MOCK_ADMIN_METRICS = {
  row1: [
    {
      val: '2,847',
      label: 'Total cases (all time)',
      delta: '+127 this month',
      up: true,
      variant: 'mc-accent',
    },
    {
      val: '91.2%',
      label: 'SLA compliance (30d)',
      delta: '−2.1% vs last month',
      up: false,
      variant: '',
    },
    {
      val: '₦84.7M',
      label: 'Revenue collected (30d)',
      delta: '+12% vs last month',
      up: true,
      variant: '',
    },
  ],
  row2: [
    { val: '342', label: 'Active cases', delta: null, variant: '' },
    { val: '94%', label: 'AI accuracy (verified)', delta: null, variant: '' },
    {
      val: '2.1h',
      label: 'Avg registrar review time',
      delta: null,
      variant: '',
    },
  ],
};

export const MOCK_BOTTLENECK_ALERTS = [
  {
    type: 'err',
    title: 'Registrar SLA breach',
    desc: 'LC/2025/00139 — Nwosu v. Alpine Ventures. 76h in queue (SLA: 72h).',
    code: 'Assigned: F. Adeyemi · Escalation: Level 1',
  },
  {
    type: 'warn',
    title: 'Judgment delay — 42 days',
    desc: 'LC/2024/00987 — Okafor v. Standard Industries. Exceeded 30-day ruling SLA.',
    code: 'Judge: Hon. J. B. Adewale · Escalation: Level 2',
  },
  {
    type: 'warn',
    title: 'Orphan payment detected',
    desc: 'RRR 340918273649 — ₦30,000 paid but no matching case record. Possible integration gap.',
    code: null,
  },
];

export const MOCK_WORKFLOW_SNAPSHOT = [
  { label: 'Draft', pct: 18, value: 23 },
  { label: 'AI Analysis', pct: 8, value: 11 },
  { label: 'Awaiting Payment', pct: 6, value: 8 },
  { label: 'Pending Registrar', pct: 5, value: 7 },
  { label: 'Correction Requested', pct: 12, value: 15 },
  { label: 'Approved', pct: 20, value: 26 },
  { label: 'Judge Assigned', pct: 32, value: 41 },
  { label: 'Hearing Scheduled', pct: 55, value: 72 },
  { label: 'Judgment Pending', pct: 15, value: 19 },
  { label: 'Closed', pct: 95, value: 2625 },
];

export const MOCK_SYSTEM_HEALTH = [
  { label: 'API', sub: 'Online · 340ms avg', status: 'ok' },
  { label: 'OCR Pipeline', sub: 'Online · 94% accuracy', status: 'ok' },
  { label: 'Remita Gateway', sub: 'Stubbed · MVP', status: 'warn' },
  { label: 'NIMC / CBN', sub: 'Stubbed · Pending', status: 'warn' },
];
